import allowedActionsDefault from './allowedActionsDefault.json'
import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { throttle } from '@repo/utils/debounce'
import MenuStructure from './MenuStructure'

const initialState = {
  menuItems: [],
  routesByPath: {},
  routesByMenuItemId: {},
  allRoutesByMenuItemId: {},
  routesByAspxPageName: {},
  mainMenuItems: null,
  showMenuHeading: false,
  rootMenuHeading: '',
  offsetTop: 0,
  offsetLeft: 0,
  offsetWidth: 0,
  minMenuHeight: 460,
  maxMenuHeight: 680,
  allowedMenuIds: [],
  allowedActions: JSON.parse(JSON.stringify(allowedActionsDefault))
}

export const useMenuItemsStore = storeWrapper('useMenuItemsStore', (set, get) => ({
  ...initialState,
  getMenuItems: async ({ routesByMenuItemId, routesByPath, allRoutesByMenuItemId = {} }) => {
    const prevMenuItems = get().menuItems
    if (prevMenuItems?.length) return

    throttle({
      id: `getMenuItems`,
      callback: async () => {
        let menuItems = await ospreyAPI({
          service: 'security',
          endpoint: 'MenuItem/GetAllAbbreviatedByCurrentUser'
        })

        let menuAreas = await ospreyAPI({
          service: 'security',
          endpoint: 'MenuItemArea/GetAll'
        })

        menuAreas = menuAreas.map((item) => {
          item.menuItemId = item.menuItemAreaId
          item.isVisible = 1
          item.isArea = true
          return item
        })

        menuItems = [...menuItems, ...menuAreas]

        const menuItemsByMenuItemId = {}
        const allowedMenuIds = []

        menuItems.forEach((menuItem) => {
          if (menuItem.aspxPageName) menuItem.aspxPageName = menuItem.aspxPageName.toLowerCase()

          if (menuItem.menuItemId) {
            const allRoutes = allRoutesByMenuItemId[menuItem.menuItemId]
            menuItem.matchingRoutes = allRoutes || []

            const routeData = routesByMenuItemId[menuItem.menuItemId]
            if (routeData) {
              menuItem = { ...menuItem, ...routeData }
              menuItem.route = routeData.path
              if (menuItem.overrideAspxPageName) menuItem.aspxPageName = menuItem.overrideAspxPageName.toLowerCase()
            }

            menuItemsByMenuItemId[menuItem.menuItemId] = menuItem

            if (!menuItem.isArea) {
              // add the menu item id to the allowed menu ids
              allowedMenuIds.push(menuItem.menuItemId)

              // add the area id to the allowed menu ids
              const areaId = menuItem.menuItemAreaId
              if (!allowedMenuIds.includes(areaId)) {
                allowedMenuIds.push(areaId)
              }
            }
          }
        })

        // add menuitem data to the route spec
        const routesByAspxPageName = {}
        Object.keys(routesByPath).forEach((path) => {
          const route = routesByPath[path]

          if (route.menuItemId) {
            const menuItem = menuItemsByMenuItemId[route.menuItemId]
            if (menuItem) {
              route.menuItemAreaId = menuItem.menuItemAreaId
              route.aspxPageName = route.overrideAspxPageName || menuItem.aspxPageName || ''
            }
          }

          if (route.aspxPageName) {
            const pageName = route.aspxPageName.toLowerCase()
            if (!routesByAspxPageName[pageName]) {
              routesByAspxPageName[pageName] = []
            }

            routesByAspxPageName[pageName].push(route)
          }

          if (route.children) {
            route.children.forEach((childRoute) => {
              if (childRoute.menuItemId) {
                const menuItem = menuItemsByMenuItemId[childRoute.menuItemId]
                if (menuItem) {
                  childRoute.menuItemAreaId = menuItem.menuItemAreaId
                  childRoute.aspxPageName = childRoute.overrideAspxPageName || menuItem.aspxPageName || ''
                }
              }
            })
          }
        })

        const menuStructureWithRoutes = recursivelyAddRoutesToMenuStructure(
          MenuStructure,
          null,
          allowedMenuIds,
          routesByPath
        )

        get().populateAllowedActions(allowedMenuIds)

        set({
          menuItems,
          menuStructureWithRoutes,
          allowedMenuIds,
          routesByPath,
          allRoutesByMenuItemId,
          routesByMenuItemId,
          routesByAspxPageName
        })
      }
    })
  },
  populateAllowedActions: (allowedMenuIds) => {
    const allowedActions = JSON.parse(JSON.stringify(allowedActionsDefault))

    Object.keys(allowedActions).forEach((key) => {
      const { menuItemId } = allowedActions[key] || {}
      if (!menuItemId) return

      if (allowedMenuIds.includes(menuItemId)) allowedActions[key].allowed = true
    })

    set({ allowedActions })
  },
  getMenuPanelItems: (pathToRootLevel = []) => {
    let menuPanelItems = get().menuStructureWithRoutes || MenuStructure

    if (pathToRootLevel?.length) {
      pathToRootLevel.forEach((pathSegment) => {
        const level = menuPanelItems.find((level) => level.urlSegment === pathSegment)
        if (level?.children?.length) menuPanelItems = level.children
      })
    }

    return menuPanelItems
  },
  openMainNavigationMenu: ({
    rootKey = '',
    showMenuHeading = false,
    rootMenuHeading = '',
    menuItems = null,
    offsetTop = 0,
    offsetLeft = 0,
    offsetWidth = 0,
    minMenuHeight = 460,
    maxMenuHeight = 680
  }) => {
    const mainMenuItems = menuItems || get().getMenuPanelItems([rootKey])

    set({
      mainMenuItems,
      showMenuHeading,
      rootMenuHeading,
      offsetTop,
      offsetLeft,
      offsetWidth,
      minMenuHeight,
      maxMenuHeight
    })
  },
  closeMainNavigationMenu: () => {
    if (get().mainMenuItems) set({ mainMenuItems: null, rootMenuHeading: null })
  },
  reset: () => {
    // perform a deep clone of the allowedActionsDefault object to ensure the state is reset
    set({ ...initialState, allowedActions: JSON.parse(JSON.stringify(allowedActionsDefault)) })
  }
}))

const hasPermittedChildren = (item, allowedMenuIds) => {
  return item.children?.some((child) => {
    return allowedMenuIds.includes(child.menuItemId) || child.hasPermittedChildren
  })
}

const recursivelyAddRoutesToMenuStructure = (menuStructure, prevRoute, allowedMenuIds, routesByPath) => {
  return menuStructure.map((item) => {
    const route = prevRoute ? `${prevRoute}/${item.urlSegment}` : item.urlSegment
    item.route = route

    const { menuItemId } = routesByPath[route] || {}
    item.menuItemId = menuItemId
    item.hasPermission = false
    item.hasPermission = menuItemId ? allowedMenuIds.includes(menuItemId) : true

    if (item.children) {
      item.children = recursivelyAddRoutesToMenuStructure(item.children, route, allowedMenuIds, routesByPath)

      const itemHasPermittedChildren = hasPermittedChildren(item, allowedMenuIds)
      item.hasPermittedChildren = itemHasPermittedChildren
    }

    return item
  })
}